import React, { useState, useEffect, useRef } from "react";
import Chart from "react-apexcharts";
import ChartErrorBoundary from "./ChartErrorBoundary";
import { withErrorSuppression } from "utils/errorSuppressionUtils";
import { Spinner } from "components";

const CustomerChartInternal = ({ series, labels, colors }) => {
  const [retryKey, setRetryKey] = useState(0);
  const [fadeIn, setFadeIn] = useState(false);
  const [chartReady, setChartReady] = useState(false);
  const initialRenderRef = useRef(true);
  const chartRef = useRef(null);

  const processData = () => {
    if (
      !Array.isArray(series) ||
      series.length === 0 ||
      !Array.isArray(labels) ||
      labels.length === 0
    ) {
      return {
        processedSeries: [25, 25, 25, 25],
        processedLabels: [
          "Category 1",
          "Category 2",
          "Category 3",
          "Category 4",
        ],
        processedColors: ["#19a8a6", "#fab54e", "#1467b0", "#19a8a6"],
        total: 100,
      };
    }

    const validSeries = series.filter(
      (val) => typeof val === "number" && !isNaN(val)
    );

    if (validSeries.length === 0) {
      return {
        processedSeries: [25, 25, 25, 25],
        processedLabels: [
          "Category 1",
          "Category 2",
          "Category 3",
          "Category 4",
        ],
        processedColors: ["#19a8a6", "#fab54e", "#1467b0", "#19a8a6"],
        total: 100,
      };
    }

    const minLength = Math.min(validSeries.length, labels.length);
    const processedSeries = validSeries.slice(0, minLength);
    const processedLabels = labels.slice(0, minLength);

    // Handle colors
    const defaultColors = ["#19a8a6", "#fab54e", "#1467b0", "#19a8a6"];
    let processedColors;

    if (Array.isArray(colors) && colors.length >= minLength) {
      processedColors = colors.slice(0, minLength);
    } else {
      processedColors = Array(minLength)
        .fill()
        .map((_, i) => defaultColors[i % defaultColors.length]);
    }

    const total = processedSeries.reduce((sum, val) => sum + val, 0) || 1;
    const percentages = processedSeries.map((val) =>
      Math.max((val / total) * 100, 0.1)
    );

    return {
      processedSeries: percentages,
      processedLabels,
      processedColors,
      total,
      originalSeries: processedSeries,
    };
  };

  useEffect(() => {
    if (initialRenderRef.current) {
      const timer = setTimeout(() => {
        setChartReady(true);
        initialRenderRef.current = false;

        setTimeout(() => {
          setFadeIn(true);
        }, 100);
      }, 500);

      return () => clearTimeout(timer);
    } else {
      setFadeIn(false);
      setChartReady(false);

      const timer = setTimeout(() => {
        setChartReady(true);

        setTimeout(() => {
          setFadeIn(true);
        }, 50);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [series, labels, colors]);

  useEffect(() => {
    const originalOnError = window.onerror;

    window.onerror = function (message, source, lineno, colno, error) {
      if (
        (source && source.includes("apexcharts")) ||
        (message && message.includes("apexcharts")) ||
        (error && error.stack && error.stack.includes("apexcharts"))
      ) {
        return true;
      }

      if (originalOnError) {
        return originalOnError(message, source, lineno, colno, error);
      }

      return false;
    };

    return () => {
      window.onerror = originalOnError;
    };
  }, []);

  const handleRetry = () => {
    setRetryKey((prev) => prev + 1);
    setFadeIn(false);
    setChartReady(false);

    setTimeout(() => {
      setChartReady(true);
      setTimeout(() => {
        setFadeIn(true);
      }, 50);
    }, 100);
  };

  if (!chartReady) {
    return (
      <div className="w-full h-full flex-1 flex items-center justify-center p-4 min-h-96">
        <Spinner size="large" color="#19a8a6" />
      </div>
    );
  }

  const {
    processedSeries,
    processedLabels,
    processedColors,
    total,
    originalSeries,
  } = processData();

  const chartOptions = {
    chart: {
      type: "radialBar",
      toolbar: { show: false },
      animations: { enabled: false },
      fontFamily: "inherit",
      background: "transparent",
      sparkline: { enabled: false },
    },
    plotOptions: {
      radialBar: {
        hollow: { size: "50%", background: "#fff" },
        track: { background: "#f2f2f2" },
        dataLabels: { show: false },
      },
    },
    colors: processedColors,
    labels: processedLabels,
    stroke: { lineCap: "round" },
    tooltip: {
      enabled: true,
      custom: function ({ seriesIndex, w }) {
        try {
          if (
            !w ||
            !w.globals ||
            !w.globals.labels ||
            !Array.isArray(w.globals.labels) ||
            seriesIndex >= w.globals.labels.length
          ) {
            return "";
          }

          const label = w.globals.labels[seriesIndex] || "Unknown";
          const percentage = processedSeries[seriesIndex]?.toFixed(2) || 0;
          const value = originalSeries
            ? originalSeries[seriesIndex]?.toFixed(2) || 0
            : 0;

          return `
            <div style="padding:6px 8px; background:#fff; border:1px solid #e0e0e0; border-radius:4px;">
              <div style="font-weight:600; margin-bottom:4px;">${label}</div>
              <div>Value: ${value}</div>
              <div>Percentage: ${percentage}%</div>
            </div>
          `;
        } catch (err) {
          console.error("Error in tooltip:", err);
          return "";
        }
      },
    },
  };

  return (
    <ChartErrorBoundary resetKey={retryKey} onRetry={handleRetry}>
      <div
        className={`w-full h-full flex-1 transition-opacity duration-300 ease-in-out ${
          fadeIn ? "opacity-100" : "opacity-0"
        }`}
      >
        {chartReady && (
          <Chart
            options={chartOptions}
            series={processedSeries}
            type="radialBar"
            width="100%"
            height="auto"
            ref={chartRef}
            key={`chart-${retryKey}-${Math.random()}`}
          />
        )}
      </div>

      <div
        className={`flex flex-col px-5 py-5 max-w-96 mx-auto transition-opacity duration-300 ease-in-out ${
          fadeIn ? "opacity-100" : "opacity-0"
        }`}
      >
        <div className="flex flex-col gap-4">
          {processedLabels.map((category, index) => (
            <div
              key={`legend-${index}`}
              className="flex justify-between items-center"
            >
              <div className="flex items-center gap-2">
                <div
                  className="size-2 rounded-full"
                  style={{ backgroundColor: processedColors[index] }}
                />
                <span className="text-[#737373]">{category}</span>
              </div>
              <div className="text-[1em] text-[#737373]">
                {originalSeries ? originalSeries[index] : 0}
              </div>
            </div>
          ))}
        </div>
      </div>
    </ChartErrorBoundary>
  );
};

// Wrap the component with error suppression HOC
const CustomerChart = withErrorSuppression(CustomerChartInternal);

export default React.memo(CustomerChart);
